/**
 * Pack loader (content-schema §1). Each age pack is a JSON module loaded through a
 * dynamic `import()` so Vite code-splits it into its own lazily-loaded chunk —
 * Age 1 never ships in the initial bundle. Loading is validated: a pack that fails
 * its schema throws rather than reaching the engine as malformed data.
 *
 * The registry is empty at the schema milestone (GAM-3); age packs register their
 * loader here as they land (GAM-9+), which is the *only* wiring adding an age needs.
 */
import { PackSchema } from './schemas/entities.js';
import type { Pack } from './schemas/entities.js';
import type { ContentError, RawPack } from './validate.js';
import { formatContentErrors, validateContent } from './validate.js';

export type AgeId = string;
export interface PackModule {
  readonly default: unknown;
}
export type PackLoader = () => Promise<PackModule>;

/**
 * Age id → lazy pack loader. Each entry is the *only* wiring adding an age needs;
 * the dynamic `import()` lets Vite code-split every pack into its own chunk.
 */
export const AGE_LOADERS: Readonly<Record<AgeId, PackLoader>> = {
  'age.primordial': () => import('./packs/age1-primordial.json'),
  'age.cambrian': () => import('./packs/age2-cambrian.json'),
  'age.terrestrial': () => import('./packs/age3-terrestrial.json'),
  'age.cognitive': () => import('./packs/age4-cognitive.json'),
  'age.agricultural': () => import('./packs/age5-agricultural.json'),
  'age.galactic': () => import('./packs/age12-galactic.json'),
  'age.intergalactic': () => import('./packs/age13-intergalactic.json'),
  'age.transcendent': () => import('./packs/age14-transcendent.json'),
};

/** Thrown by {@link loadPack} / {@link loadAllPacks} when content is invalid. */
export class ContentValidationError extends Error {
  readonly errors: readonly ContentError[];
  constructor(errors: readonly ContentError[]) {
    super(
      `content validation failed with ${String(errors.length)} error(s):\n${formatContentErrors(errors)}`,
    );
    this.name = 'ContentValidationError';
    this.errors = errors;
  }
}

/** Load and schema-validate a single pack. Throws {@link ContentValidationError} on failure. */
export async function loadPack(id: AgeId, loader: PackLoader): Promise<Pack> {
  const module = await loader();
  const parsed = PackSchema.safeParse(module.default);
  if (!parsed.success) {
    throw new ContentValidationError(
      parsed.error.issues.map((issue) => ({
        pack: id,
        id,
        field: issue.path.length > 0 ? issue.path.join('.') : '(root)',
        message: issue.message,
      })),
    );
  }
  return parsed.data;
}

/** The global pack (prestige layers, achievements) — loaded alongside age packs so
 * cross-pack references (achievements/keepsakes → age content, endgame → prestige)
 * resolve at runtime exactly as they do in the `validate:content` gate. */
export const loadGlobalPack: PackLoader = () => import('./packs/global.json');

/**
 * Load every registered age pack plus the global pack and run the full
 * referential-integrity pass over them together. Throws {@link ContentValidationError}
 * if any pack — or any cross-pack reference — is invalid. Returns the age packs.
 */
export async function loadAllPacks(
  loaders: Readonly<Record<AgeId, PackLoader>> = AGE_LOADERS,
  globalLoader: PackLoader = loadGlobalPack,
): Promise<Pack[]> {
  const entries = Object.entries(loaders);
  const agePacks: RawPack[] = await Promise.all(
    entries.map(async ([id, loader]) => ({ name: id, data: (await loader()).default })),
  );
  const global: RawPack = {
    name: 'global.json',
    data: (await globalLoader()).default,
    global: true,
  };
  const errors = validateContent([...agePacks, global]);
  if (errors.length > 0) throw new ContentValidationError(errors);
  return agePacks.map((r) => PackSchema.parse(r.data));
}
